import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const SELLING_PLAN_SYNC_EVENT = 'selling-plan:sync';
const SELLING_PLAN_SYNC_SOURCE_SUBSCRIPTION = 'subscription-plan-comparison';

class SubscriptionPlanComparisonComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);

    this.#eventTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#eventTarget?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#syncTarget = this.closest('.shopify-section, dialog, product-card') || document;
    this.#syncTarget.addEventListener(SELLING_PLAN_SYNC_EVENT, this.#handleExternalSellingPlanSync);

    this.#syncInitialSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#eventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#syncTarget.removeEventListener(SELLING_PLAN_SYNC_EVENT, this.#handleExternalSellingPlanSync);
  }

  /** @type {HTMLElement | null} */
  #eventTarget = null;

  /** @type {HTMLElement | Document} */
  #syncTarget = document;

  #handleClick = (event) => {
    const infoTrigger = event.target instanceof Element ? event.target.closest('[data-free-product-info-trigger]') : null;
    if (infoTrigger instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#openFreeProductDrawer(infoTrigger.dataset.freeProductDrawerId || '');
      return;
    }

    let target = event.target instanceof Element ? event.target.closest('[data-plan-card]') : null;
    if (!(target instanceof HTMLElement) && event.target instanceof Element) {
      const planContainer = event.target.closest('.subscription-plan-comparison__plan-container');
      target = planContainer?.querySelector('[data-plan-card]') || null;
    }
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();

    const sellingPlanId = target.dataset.sellingPlanId || '';
    const variantId = target.dataset.variantId || '';
    this.#selectPlan(sellingPlanId, { variantId });
  };

  #handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const infoTrigger = event.target.closest('[data-free-product-info-trigger]');
    if (!(infoTrigger instanceof HTMLElement)) return;

    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    event.stopPropagation();
    this.#openFreeProductDrawer(infoTrigger.dataset.freeProductDrawerId || '');
  };

  /**
   * @param {string} drawerId
   */
  #openFreeProductDrawer(drawerId) {
    if (!drawerId) return;

    const drawerElement = document.getElementById(drawerId);
    if (!(drawerElement instanceof HTMLElement)) return;

    if (typeof drawerElement.showDialog === 'function') {
      drawerElement.showDialog();
      return;
    }

    const drawer = drawerElement.closest('dialog-component');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  #syncInitialSelection() {
    const input = this.#sellingPlanInput;
    const cards = this.#planCards;
    if (!input || cards.length === 0) return;

    const initialPlanId = input.value || cards[0].dataset.sellingPlanId || '';
    let selectedVariantId = this.#linkedVariantInput?.value || cards[0].dataset.variantId || '';

    if (initialPlanId) {
      const initialPlanCard = cards.find((card) => card.dataset.sellingPlanId === initialPlanId) || null;
      if (initialPlanCard?.dataset.variantId) {
        selectedVariantId = initialPlanCard.dataset.variantId;
      }
    }

    this.#selectPlan(initialPlanId, { variantId: selectedVariantId });
  }

  /**
   * @param {string} sellingPlanId
   * @param {{ variantId?: string; shouldDispatchInputChange?: boolean; shouldBroadcast?: boolean }} [options]
   */
  #selectPlan(sellingPlanId, options = {}) {
    const input = this.#sellingPlanInput;
    const cards = this.#planCards;
    if (!input || cards.length === 0) return;

    const requestedVariantId = options.variantId || '';
    const shouldDispatchInputChange = options.shouldDispatchInputChange ?? true;
    const shouldBroadcast = options.shouldBroadcast ?? true;

    let selectedCard = null;
    if (sellingPlanId) {
      selectedCard = cards.find((card) => {
        if (card.dataset.sellingPlanId !== sellingPlanId) return false;
        if (requestedVariantId && card.dataset.variantId && card.dataset.variantId !== requestedVariantId) return false;
        return true;
      });

      if (!selectedCard) {
        selectedCard = cards.find((card) => card.dataset.sellingPlanId === sellingPlanId) || null;
      }
    }

    if (!selectedCard && requestedVariantId) {
      selectedCard = cards.find((card) => card.dataset.variantId === requestedVariantId) || null;
    }

    if (!selectedCard) {
      selectedCard = cards[0] || null;
    }

    if (!selectedCard) return;

    const selectedPlanId = selectedCard.dataset.sellingPlanId || '';
    const selectedVariantId = selectedCard.dataset.variantId || '';
    const selectedVariantIsAvailable = this.#isVariantAvailable(selectedCard);

    cards.forEach((card) => {
      const isActive = card === selectedCard;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-pressed', String(isActive));

      const radio = card.querySelector('[data-plan-radio]');
      if (radio instanceof HTMLElement) {
        radio.classList.toggle('is-active', isActive);
      }
    });

    this.#syncLinkedVariantInput(selectedVariantId);
    this.#syncAddToCartState(selectedVariantIsAvailable);

    input.value = selectedPlanId;
    if (shouldDispatchInputChange) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (shouldBroadcast) {
      this.#syncTarget.dispatchEvent(
        new CustomEvent(SELLING_PLAN_SYNC_EVENT, {
          bubbles: true,
          detail: {
            source: SELLING_PLAN_SYNC_SOURCE_SUBSCRIPTION,
            productId: this.dataset.productId || '',
            sellingPlanId: selectedPlanId,
            variantId: selectedVariantId,
          },
        })
      );
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleExternalSellingPlanSync = (event) => {
    if (!event?.detail) return;
    if (event.detail.source === SELLING_PLAN_SYNC_SOURCE_SUBSCRIPTION) return;
    if (`${event.detail.productId || ''}` !== `${this.dataset.productId || ''}`) return;

    this.#selectPlan(`${event.detail.sellingPlanId || ''}`, {
      variantId: `${event.detail.variantId || ''}`,
      shouldDispatchInputChange: false,
      shouldBroadcast: false,
    });
  };

  #handleVariantUpdate = (event) => {
    if (!event?.detail?.data?.html) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const previouslySelectedPlanId = this.#sellingPlanInput?.value || '';
    const newComponent = event.detail.data.html.querySelector(
      `subscription-plan-comparison-component[data-block-id="${blockId}"]`
    );

    if (!newComponent) {
      this.#handleNoPlansState();
      return;
    }

    this.hidden = false;
    this.removeAttribute('hidden');

    morph(this, newComponent, { childrenOnly: true });
    if (newComponent instanceof HTMLElement) {
      this.dataset.productId = newComponent.dataset.productId || this.dataset.productId;
      this.dataset.blockId = newComponent.dataset.blockId || this.dataset.blockId;
    }

    const nextSelectedPlanId = previouslySelectedPlanId || this.#sellingPlanInput?.value || '';
    const nextSelectedVariantId = this.#linkedVariantInput?.value || '';
    this.#selectPlan(nextSelectedPlanId, { variantId: nextSelectedVariantId });
  };

  #handleNoPlansState() {
    this.hidden = true;
    this.setAttribute('hidden', '');

    const input = this.#sellingPlanInput;
    if (input && input.value !== '') {
      input.value = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#syncTarget.dispatchEvent(
      new CustomEvent(SELLING_PLAN_SYNC_EVENT, {
        bubbles: true,
        detail: {
          source: SELLING_PLAN_SYNC_SOURCE_SUBSCRIPTION,
          productId: this.dataset.productId || '',
          sellingPlanId: '',
          variantId: '',
        },
      })
    );
  }

  get #planCards() {
    return Array.from(this.querySelectorAll('[data-plan-card]'));
  }

  /**
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  #isVariantAvailable(card) {
    const value = `${card.dataset.variantAvailable || ''}`.toLowerCase();
    if (!value) return true;
    return value !== 'false' && value !== '0';
  }

  get #sellingPlanInput() {
    const input = this.querySelector('[data-selling-plan-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  get #linkedVariantInput() {
    const input = this.#sellingPlanInput;
    if (!input) return null;

    const formId = input.getAttribute('form') || input.form?.id || '';
    if (!formId) return null;

    const variantInput = this.#syncTarget.querySelector(`form[id="${formId}"] input[name="id"]`);
    return variantInput instanceof HTMLInputElement ? variantInput : null;
  }

  /**
   * @param {string} variantId
   */
  #syncLinkedVariantInput(variantId) {
    if (!variantId) return;

    const variantInput = this.#linkedVariantInput;
    const currentVariantId = `${variantInput?.value || ''}`;
    if (currentVariantId === variantId) return;

    const didRequestVariantChange = this.#syncVariantPickerSelection(variantId);
    if (variantInput) {
      variantInput.value = variantId;
    }

    if (!didRequestVariantChange && variantInput) {
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Keep main variant picker in sync so sticky bar variant + sticky plan selector update together.
   * @param {string} variantId
   * @returns {boolean}
   */
  #syncVariantPickerSelection(variantId) {
    if (!variantId) return false;

    const productId = `${this.dataset.productId || ''}`;
    const scopedPicker =
      this.#syncTarget.querySelector(`variant-picker[data-product-id="${productId}"]`) ||
      this.#syncTarget.querySelector('variant-picker');
    if (!(scopedPicker instanceof HTMLElement)) return false;

    const candidates = Array.from(scopedPicker.querySelectorAll('[data-variant-id]')).filter(
      (element) => element instanceof HTMLElement && `${element.dataset.variantId || ''}` === variantId
    );
    if (candidates.length === 0) return false;

    const isSelectedCandidate = (candidate) => {
      if (candidate instanceof HTMLInputElement) return candidate.checked;
      if (candidate instanceof HTMLOptionElement) return candidate.selected;
      return false;
    };

    const candidate = candidates.find((entry) => !isSelectedCandidate(entry)) || candidates[0];
    if (candidate instanceof HTMLInputElement) {
      if (!candidate.checked) {
        candidate.click();
      } else {
        candidate.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    if (candidate instanceof HTMLOptionElement) {
      const select = candidate.parentElement;
      if (!(select instanceof HTMLSelectElement)) return false;

      if (select.value === candidate.value && candidate.selected) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      select.value = candidate.value;
      candidate.selected = true;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  /**
   * Toggle add-to-cart state based on selected subscription variant availability.
   * @param {boolean} isAvailable
   */
  #syncAddToCartState(isAvailable) {
    const linkedFormId = this.#sellingPlanInput?.getAttribute('form') || this.#sellingPlanInput?.form?.id || '';
    const linkedForm =
      (linkedFormId && this.#syncTarget.querySelector(`form[id="${linkedFormId}"]`)) || this.#sellingPlanInput?.form || null;
    const productFormComponent =
      this.#linkedVariantInput?.closest('product-form-component') || linkedForm?.closest('product-form-component');
    if (!(productFormComponent instanceof HTMLElement)) return;

    const addToCartComponents = productFormComponent.querySelectorAll('add-to-cart-component');
    addToCartComponents.forEach((component) => {
      if (typeof component.disable !== 'function' || typeof component.enable !== 'function') return;
      const button = component.refs?.addToCartButton;
      if (!(button instanceof HTMLButtonElement)) return;

      if (isAvailable) {
        component.enable();
        this.#setOutOfStockLabel(button, false);
      } else {
        component.disable();
        this.#setOutOfStockLabel(button, true);
      }
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} shouldShowOutOfStock
   */
  #setOutOfStockLabel(button, shouldShowOutOfStock) {
    const desktopText = button.querySelector('.desktop_text');
    const mobileText = button.querySelector('.mobile_text');

    const defaultDesktopText = button.dataset.defaultDesktopText || 'continue to checkout';
    const defaultMobileText = button.dataset.defaultMobileText || '';
    const outOfStockText = button.dataset.outOfStockText || 'Out of stock';
    const outOfStockLabel = `${outOfStockText}`;

    if (desktopText instanceof HTMLElement) {
      desktopText.textContent = shouldShowOutOfStock ? outOfStockLabel : defaultDesktopText;
    }

    if (mobileText instanceof HTMLElement) {
      mobileText.textContent = shouldShowOutOfStock ? outOfStockLabel : defaultMobileText;
    }
  }
}

if (!customElements.get('subscription-plan-comparison-component')) {
  customElements.define('subscription-plan-comparison-component', SubscriptionPlanComparisonComponent);
}
